import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Home,
  LockKeyhole,
  LogOut,
  RefreshCcw,
  ServerOff,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import img404 from "@/assets/mascot-404.png";
import img500 from "@/assets/mascot-500.png";
import img502 from "@/assets/mascot-502.png";
import { getLandingHomePath } from "@/lib/runtime";
import {
  GENERIC_STATUS_CODES,
  REASON_ACTIONS,
  getErrorCopy,
  isErrorReason,
  resolveErrorLang,
  type ErrorAction,
  type ErrorReason,
  type ErrorUiCopy,
} from "@/lib/error-page-copy";

// Image paths for Foxia fox mascot
const IMG_404 = img404;
const IMG_500 = img500;
const IMG_502 = img502;

const GENERIC_PRESENTATION = {
  404: { img: IMG_404, bg: "bg-orange-50" },
  500: { img: IMG_500, bg: "bg-orange-50" },
  502: { img: IMG_502, bg: "bg-amber-50" },
  503: { img: IMG_502, bg: "bg-amber-50" },
} as const;

const REASON_ICONS: Record<
  ErrorReason,
  { Icon: LucideIcon; iconClass: string }
> = {
  session_expired: { Icon: LockKeyhole, iconClass: "bg-orange-50 text-orange-600" },
  workspace_not_found: { Icon: Building2, iconClass: "bg-amber-50 text-amber-600" },
  access_denied: { Icon: ShieldAlert, iconClass: "bg-orange-50 text-orange-600" },
  auth_retry: { Icon: RefreshCcw, iconClass: "bg-amber-50 text-amber-600" },
  auth_failed: { Icon: LockKeyhole, iconClass: "bg-orange-50 text-orange-600" },
  workspace_mismatch: { Icon: Building2, iconClass: "bg-amber-50 text-amber-600" },
  workspace_unavailable: { Icon: ServerOff, iconClass: "bg-amber-50 text-amber-600" },
};

// Mã kỹ thuật lấy từ query string: chỉ giữ ký tự an toàn, không hiển thị mô tả thô.
const SAFE_CODE_PATTERN = /^[a-z0-9_.-]{1,64}$/i;

interface ErrorPageProps {
  code?: number;
  title?: string;
  description?: string;
  /** Dùng nội dung "router error" đã dịch thay vì truyền chuỗi cố định. */
  variant?: "route_error";
}

interface ErrorLocationState {
  code?: number;
}

interface ReasonActionsProps {
  actions: ErrorAction[];
  ui: ErrorUiCopy;
  loginHref: string;
  switchAccountHref: string;
  switchWorkspaceHref: string;
  onRetryReturnTo: () => void;
}

function ReasonActions({
  actions,
  ui,
  loginHref,
  switchAccountHref,
  switchWorkspaceHref,
  onRetryReturnTo,
}: ReasonActionsProps) {
  return (
    <>
      {actions.map((action, index) => {
        const variant = index === 0 ? "default" : index === 1 ? "outline" : "ghost";
        const className =
          index === 0
            ? "h-10 w-full"
            : index === 1
              ? "h-10 w-full"
              : "h-9 w-full text-zinc-600";
        switch (action) {
          case "login":
            return (
              <Button key={action} asChild variant={variant} className={className}>
                <a href={loginHref}>{ui.login}</a>
              </Button>
            );
          case "retry_login":
            return (
              <Button key={action} asChild variant={variant} className={className}>
                <a href={loginHref}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  {ui.retry}
                </a>
              </Button>
            );
          case "retry_return_to":
            return (
              <Button key={action} variant={variant} className={className} onClick={onRetryReturnTo}>
                <RefreshCcw className="mr-2 h-4 w-4" />
                {ui.retry}
              </Button>
            );
          case "switch_account":
            return (
              <Button key={action} asChild variant={variant} className={className}>
                <a href={switchAccountHref}>
                  <LogOut className="mr-2 h-4 w-4" />
                  {ui.switchAccount}
                </a>
              </Button>
            );
          case "switch_workspace":
            return (
              <Button key={action} asChild variant={variant} className={className}>
                <a href={switchWorkspaceHref}>
                  <Building2 className="mr-2 h-4 w-4" />
                  {ui.switchWorkspace}
                </a>
              </Button>
            );
          case "home":
            return (
              <Button key={action} asChild variant={variant} className={className}>
                <a href={getLandingHomePath()}>
                  <Home className="mr-2 h-4 w-4" />
                  {ui.homeLong}
                </a>
              </Button>
            );
        }
      })}
    </>
  );
}

export default function ErrorPage({
  code: propsCode,
  title: propsTitle,
  description: propsDescription,
  variant,
}: ErrorPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as ErrorLocationState | null;
  const queryParams = new URLSearchParams(location.search);
  const copy = getErrorCopy(resolveErrorLang(location.search));
  const { ui } = copy;
  const rawReason = queryParams.get("reason");
  const reason = isErrorReason(rawReason) ? rawReason : null;
  const rawTechnicalCode = queryParams.get("error");
  const technicalCode =
    rawTechnicalCode && SAFE_CODE_PATTERN.test(rawTechnicalCode)
      ? rawTechnicalCode
      : null;
  // return_to đến từ query string. Nút "Thử lại" điều hướng thẳng tới giá trị
  // này, nên chỉ chấp nhận đường dẫn nội bộ để không mở sang tên miền ngoài.
  const rawReturnTo = queryParams.get("return_to");
  const sessionExpiredReturnTo =
    rawReturnTo &&
    rawReturnTo.startsWith("/") &&
    !rawReturnTo.startsWith("//") &&
    !rawReturnTo.startsWith("/\\")
      ? rawReturnTo
      : "/web";
  const encodedReturnTo = encodeURIComponent(sessionExpiredReturnTo);
  const loginHref = `/api/gateway/auth/login?return_to=${encodedReturnTo}`;
  const switchAccountHref = `/web/logout?switch_account=1&return_to=${encodedReturnTo}`;
  const switchWorkspaceHref = `/api/gateway/auth/login?select_workspace=1&return_to=${encodedReturnTo}`;
  const handleRetry = () => {
    const retryUrl = `${location.pathname}${location.search}${location.hash}`;
    window.location.assign(retryUrl);
  };
  // Trang lỗi này đến bằng chuyển hướng, nên nạp lại chính nó chỉ vẽ lại lỗi.
  // Thử lại phải quay về đường dẫn ban đầu đã lưu trong return_to.
  const handleRetryReturnTo = () => {
    window.location.assign(sessionExpiredReturnTo);
  };

  // Try to get status from props, location state or ?code=
  const queryCode = Number.parseInt(queryParams.get("code") ?? "", 10);
  const status =
    propsCode ||
    locationState?.code ||
    (Number.isNaN(queryCode) ? 0 : queryCode) ||
    404;
  const genericStatus = (GENERIC_STATUS_CODES as readonly number[]).includes(status)
    ? (status as keyof typeof GENERIC_PRESENTATION)
    : null;

  const defaultTitle =
    variant === "route_error"
      ? ui.routeErrorTitle
      : genericStatus
        ? copy.generic[genericStatus].title
        : ui.unknownTitle;
  const defaultDescription =
    variant === "route_error"
      ? ui.routeErrorDescription
      : genericStatus
        ? copy.generic[genericStatus].description
        : ui.unknownDescription;
  const title = propsTitle || defaultTitle;
  const description = propsDescription || defaultDescription;
  const { img, bg } = genericStatus
    ? GENERIC_PRESENTATION[genericStatus]
    : { img: IMG_500, bg: "bg-orange-50" };

  if (reason) {
    const reasonCopy = copy.reasons[reason];
    const { Icon, iconClass } = REASON_ICONS[reason];
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-50 p-4">
        <meta name="robots" content="noindex, nofollow" />
        <Card className="w-full max-w-md border border-zinc-200 bg-white shadow-sm">
          <CardHeader className="items-center px-6 pb-2 pt-8 text-center">
            <span
              className={`flex h-12 w-12 items-center justify-center rounded-full ${iconClass}`}
              aria-hidden="true"
            >
              <Icon className="h-6 w-6" strokeWidth={1.75} />
            </span>
            <CardTitle className="mt-5 text-2xl font-semibold tracking-normal text-zinc-900">
              {reasonCopy.title}
            </CardTitle>
            <CardDescription className="mt-2 max-w-sm text-sm leading-relaxed text-zinc-600">
              {reasonCopy.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="px-6 pt-4">
            <p className="text-center text-sm leading-relaxed text-zinc-500">
              {reasonCopy.hint}
            </p>
            {technicalCode && (
              <details className="mt-3 text-center text-xs text-zinc-400">
                <summary className="cursor-pointer">{ui.technicalDetail}</summary>
                <p className="mt-1">
                  {ui.codeLabel}: <code>{technicalCode}</code>
                </p>
              </details>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-2 px-6 pb-8 pt-6">
            <ReasonActions
              actions={REASON_ACTIONS[reason]}
              ui={ui}
              loginHref={loginHref}
              switchAccountHref={switchAccountHref}
              switchWorkspaceHref={switchWorkspaceHref}
              onRetryReturnTo={handleRetryReturnTo}
            />
          </CardFooter>
        </Card>
        <p className="mt-6 max-w-sm text-center text-sm text-zinc-500">
          {reasonCopy.footer}
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-zinc-50 p-4">
      <meta name="robots" content="noindex, nofollow" />
      <Card className="w-full max-w-md overflow-hidden border border-zinc-200 bg-white shadow-sm">
        <div
          className={`relative h-48 w-full ${bg} flex items-center justify-center overflow-hidden`}
        >
          <img
            src={img}
            alt={title}
            className="h-full w-full object-cover"
          />
        </div>
        <CardHeader className="px-6 pb-2 pt-6 text-center">
          <CardTitle className="text-2xl font-semibold tracking-normal text-zinc-900">
            {status} - {title}
          </CardTitle>
          <CardDescription className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-zinc-600">
            {description}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-6 pb-2">
          <div className="h-px w-full bg-zinc-100" />
        </CardContent>
        <CardFooter className="flex flex-col gap-2 px-6 pb-8 pt-4">
          <Button
            className="h-10 w-full"
            onClick={handleRetry}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            {ui.retry}
          </Button>
          <div className="flex w-full gap-2">
            <Button
              variant="outline"
              className="h-10 flex-1"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {ui.back}
            </Button>
            <Button
              variant="outline"
              className="h-10 flex-1"
              onClick={() => (window.location.href = "/")}
            >
              <Home className="mr-2 h-4 w-4" />
              {ui.home}
            </Button>
          </div>
          <Button
            variant="ghost"
            className="h-9 w-full text-zinc-600"
            onClick={() => (window.location.href = "/web/logout")}
          >
            <LogOut className="mr-2 h-4 w-4" />
            {ui.logout}
          </Button>
        </CardFooter>
      </Card>

      <p className="mt-6 max-w-sm text-center text-sm text-zinc-500">
        {ui.generalFooter}
      </p>
    </div>
  );
}
